import { useToast } from "@chatfood/design-system";
import { isBasketRepoError } from "@chatfood/dbe-client/errors";
import { useLocale } from "~/composables/use-locale";
import type { IDomainItem } from "#imports";
import { useServiceError } from "~/composables/use-service-error";

export const useAddItemBasketErrorValidation = () => {
  const { toastError } = useToast();
  const { t } = useLocale();
  const { handleBusinessRuleError } = useServiceError();

  const findModifierListName = (
    modifierLists: IDomainItem["modifierLists"],
    targetId: string,
    itemId?: string,
  ): string | undefined => {
    for (const list of modifierLists) {
      if (list.id === targetId) return list.name;

      // Search in nested modifier lists
      for (const modifier of list.modifiers) {
        if (modifier.modifierLists?.length && modifier.id !== itemId) {
          const found = findModifierListName(modifier.modifierLists, targetId);
          if (found) return found;
        }
      }
    }
  };

  const handleBasketError = (
    error: unknown,
    modifierLists: IDomainItem["modifierLists"],
    itemId?: string,
  ) => {
    if (!isBasketRepoError(error) || !error.isBusinessRule()) {
      return false;
    }

    const { modifierListId, minRequired, maxAllowed, actualCount } =
      error.meta?.context || {};

    const modifierListName =
      findModifierListName(modifierLists, modifierListId, itemId) ||
      "modifier list";

    if (actualCount > maxAllowed) {
      toastError(
        t(
          "composables.use_add_item_basket_error_validation.max_allowed_error",
          {
            modifierListName,
            maxAllowed,
          },
        ),
      );
      return true;
    }

    if (actualCount < minRequired) {
      toastError(
        t(
          "composables.use_add_item_basket_error_validation.min_required_error",
          {
            modifierListName,
            minRequired,
          },
        ),
      );
      return true;
    }

    // Add-to-basket errors fire from inside the item modal; a toast floats
    // above it, whereas a modal would render behind it.
    handleBusinessRuleError(error, { via: "toast" });
    return true;
  };

  return {
    handleBasketError,
  };
};
